// src/sections/Hero.jsx
import { Link } from "react-router-dom";
import Container from "../components/ui/Container";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";

export default function Hero() {
    return (
        <section className="relative overflow-hidden">
            {/* Hintergrund: sanfte Verläufe + Grid */}
            <div data-aos="fade-up" data-aos-delay="600" data-aos-duration="900" className="pointer-events-none absolute inset-0">
                <div className="absolute -top-32 -left-24 h-72 w-72 rounded-full bg-brand-300/25 blur-3xl" />
                <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-brand-200/20 blur-3xl" />
                <div className="absolute inset-0 bg-gradient-to-b from-brand-50/60 to-transparent dark:from-brand-900/10" />
                <div
                    aria-hidden="true"
                    className="absolute inset-0 [mask-image:radial-gradient(300px_200px_at_center,black,transparent)]"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, rgba(100,116,139,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(100,116,139,0.08) 1px, transparent 1px)",
                        backgroundSize: "28px 28px",
                    }}
                />
            </div>

            <Container className="relative py-14 sm:py-20 lg:py-28">
                <div data-aos="fade-up" data-aos-duration="1000" className="grid items-center gap-12 lg:grid-cols-2">
                    {/* Textspalte */}
                    <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
                        <Badge className="bg-brand-600 text-white dark:bg-brand-500 px-5 py-2 shadow-lg shadow-brand-500/25">
                            ✓ Seit über 40 Jahren Qualität
                        </Badge>

                        <h1 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-heading font-black leading-[1.05] tracking-tight text-slate-900 dark:text-white">
                            Betonrohrfallen
                            <span className="block text-brand-600 mt-1">&amp; Zubehör</span>
                        </h1>
                        <p className="mt-4 text-lg sm:text-xl text-slate-500 dark:text-slate-400 font-medium tracking-wide uppercase">
                            robust · zuverlässig · praxiserprobt
                        </p>

                        <p className="mt-6 max-w-xl text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
                            Meine Betonrohrfalle ist das Ergebnis jahrzehntelanger Erfahrung. Sie fängt rund um die Uhr – bei jedem Wetter, absolut sicher.
                        </p>

                        {/* Bullet-USP */}
                        <ul className="mt-8 grid gap-3 text-sm sm:text-base text-slate-600 dark:text-slate-300">
                            {[
                                "Massive Bauweise & leichter Aufbau",
                                "DHL & Spedition – DE/AT",
                                "Kompetente Beratung aus der Praxis",
                            ].map((t) => (
                                <li key={t} className="flex items-center gap-3">
                                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
                                        <svg className="h-3 w-3 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>

                        {/* CTAs */}
                        <div className="mt-10 flex flex-wrap justify-center lg:justify-start gap-4">
                            <Button as={Link} to="/shop" variant="solid" className="px-8 py-4 text-lg shadow-lg shadow-brand-500/15 hover:scale-[1.02] active:scale-95 transition-all">
                                Zum Shop
                            </Button>
                            <Button as={Link} to="/kontakt" variant="outline" className="px-8 py-4 text-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                Beratung anfragen
                            </Button>
                        </div>

                        {/* Trust-Row */}
                        <div className="mt-10 pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-wrap justify-center lg:justify-start items-center gap-6">
                            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-slate-800">
                                <svg className="h-4 w-4 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                                <span className="text-xs font-bold text-slate-600 dark:text-slate-300">SSL &amp; PayPal</span>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-slate-800">
                                <svg className="h-4 w-4 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"/></svg>
                                <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Schneller Versand</span>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-slate-800">
                                <svg className="h-4 w-4 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Persönlicher Support</span>
                            </div>
                        </div>
                    </div>

                    {/* Bildspalte */}
                    <div data-aos="zoom-in-up" data-aos-duration="1000" className="relative group">
                        <div className="relative rounded-[2rem] border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl p-4 transition-transform group-hover:scale-[1.01]">
                            <div className="h-64 sm:h-[380px] lg:h-[440px] w-full overflow-hidden rounded-2xl bg-slate-50 dark:bg-slate-800/50 flex items-center justify-center">
                                <img
                                    src="/images/hero.webp"
                                    alt="THB Systeme – Betonrohrfallen & Zubehör"
                                    className="h-full w-full object-contain"
                                    loading="eager"
                                />
                            </div>
                            {/* Overlay-Badge */}
                            <div className="absolute -bottom-6 -left-2 sm:-left-4">
                                <div className="rounded-2xl bg-white/90 dark:bg-slate-900/90 backdrop-blur border border-slate-200 dark:border-slate-800 shadow-xl px-5 py-3">
                                    <div className="text-[10px] uppercase tracking-wider text-brand-600 font-bold">Echtes Handwerk</div>
                                    <div className="mt-1 flex items-center gap-3">
                                        <div className="font-bold text-slate-900 dark:text-white">Helmut Tuscher</div>
                                        <div className="flex text-amber-400">★★★★★</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Deko-Elemente */}
                        <div className="absolute -z-10 -top-4 -right-4 h-24 w-24 bg-brand-100 dark:bg-brand-900/20 rounded-full blur-2xl opacity-50" />
                        <div className="absolute -z-10 -bottom-4 -left-4 h-32 w-32 bg-brand-200 dark:bg-brand-800/10 rounded-full blur-3xl opacity-40" />
                    </div>
                </div>
            </Container>
        </section>
    );
}
